// client/src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [viewMode, setViewMode] = useState('default') // 'default' | 'customer' | 'impersonating'
  const [realUser, setRealUser] = useState(null)      // original user while in a view mode

  const login = useCallback((accessToken, userData) => {
    localStorage.setItem('accessToken', accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    setUser(userData)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
    setViewMode('default')
    setRealUser(null)
  }, [])

  // Garage/admin users can preview their own customer-facing data for 2 hours.
  const switchToCustomerView = useCallback(async () => {
    const { data } = await api.post('/auth/switch-view')
    setRealUser(user)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
    setUser(data.user)
    setViewMode('customer')
  }, [user])

  // Platform admin can open a 1-hour session as any other user.
  const impersonateUser = useCallback(async (targetUserId) => {
    const { data } = await api.post('/auth/impersonate', { targetUserId })
    setRealUser(user)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
    setUser(data.user)
    setViewMode('impersonating')
  }, [user])

  // Restore the original session — reads the real token from localStorage.
  const restoreView = useCallback(() => {
    const realToken = localStorage.getItem('accessToken')
    api.defaults.headers.common['Authorization'] = `Bearer ${realToken}`
    setUser(realUser)
    setViewMode('default')
    setRealUser(null)
  }, [realUser])

  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      setLoading(false)
      return
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`

    api.get('/auth/me')
      .then(({ data }) => setUser(data))
      .catch(async (err) => {
        if (err.response?.data?.code === 'TOKEN_EXPIRED') {
          try {
            const { data } = await api.post('/auth/refresh')
            localStorage.setItem('accessToken', data.accessToken)
            api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
            setUser(data.user)
          } catch {
            localStorage.removeItem('accessToken')
            delete api.defaults.headers.common['Authorization']
          }
        } else {
          localStorage.removeItem('accessToken')
          delete api.defaults.headers.common['Authorization']
        }
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      viewMode, realUser, switchToCustomerView, impersonateUser, restoreView,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
